import React, { useState, PropsWithChildren, useEffect, useMemo } from 'react';

import { BundleRecommendationContext } from './bundle-recommendation-context';
import { BundlesRepository } from '../../features/bundles/repository';
import { LocalStorage } from '../../utils';
import { RecommendableBundle } from '../../domain/bundles/recommendable-bundle';

import useShoppingCartContext from '../ShoppingCart/use-shopping-cart-context';
import { isBundleDisclaimerEnabled, isBundlesRecommendationEnabled } from '../../features/bundles/repository/bundles.repository.config';
import { useCurrentPosition } from '../../hooks';
import { BundleDisclaimerResponse, LOCAL_STORAGE_SHOWN_BUNDLE_ALERT } from '../../domain/bundles/disclaimer-bundle';

const bundlesRepository = new BundlesRepository();

// ********************************************************************************
export const BundleRecommendationProvider = ({ children }: PropsWithChildren) => {
  const { cart, applyBundle } = useShoppingCartContext();
  // Ask to the user for permission to get the location
  const { currentPosition } = useCurrentPosition({
    disabled: !isBundlesRecommendationEnabled(),
  });

  const [recommendation, setRecommendation] = useState<RecommendableBundle | null>(null);
  const [disclaimer, setDisclaimer] = useState<BundleDisclaimerResponse | null>(null);
  
  const fetchRecommendation = async () => {
    if (!isBundlesRecommendationEnabled()) {
      return;
    }

    const location = currentPosition?.coords;
    const contactInformation = LocalStorage.getItem('CBX_Contact_Information');

    try {
      const requestBundlePayload = {
        cart: cart.items,
        location,
        zipCode: contactInformation?.zipCode,
      };
      const response = await bundlesRepository.requestRecommendation(requestBundlePayload);
      setRecommendation(response);
    } catch (error) {
      console.error('Error fetching recommendations', error);
    }
  };

  const fetchBundleDisclaimer = async () => {
    const hasShown = LocalStorage.getItem(LOCAL_STORAGE_SHOWN_BUNDLE_ALERT);
  
    if (!isBundleDisclaimerEnabled() || hasShown /* this means that the request was already done */) {
      return;
    }
    const location = currentPosition?.coords;
    try {
      const response = await bundlesRepository.requestBundleDisclaimer(location);
      setDisclaimer(response);
      // Only show the disclaimer if the user has not seen it before
      LocalStorage.setItem(LOCAL_STORAGE_SHOWN_BUNDLE_ALERT, 'true');

    } catch (error) {
      console.error('Error fetching disclaimers', error);
    }
  };

  useEffect(() => {
    if (cart?.items?.length === 0) {
      setRecommendation(null);
      return;
    }

    if (cart.items && cart?.items?.length > 0) {
      fetchRecommendation();
    }
  }, [cart?.items?.length, currentPosition]);

  useEffect(() => {
    if (!currentPosition) {
      return;
    }

    fetchBundleDisclaimer();
  }, [currentPosition]);

  useEffect(() => {
    if (!recommendation) {
      return;
    }
    // When exist an applicable bundle, call the applyBundle function
    // this modify the cart adding properties from the bundle
    // aplying the discounts.
    if (recommendation?.applicable) {
      applyBundle(recommendation.bundle);
    }
  }, [recommendation]);

  const ticketMessages = useMemo(() => {
    if (!recommendation?.messages?.ticket) {
      return null;
    }
    return recommendation?.messages?.ticket;
  }, [recommendation]);

  const parkingMessages = useMemo(() => {
    if (!recommendation?.messages?.parking) {
      return null;
    }
    return recommendation?.messages?.parking;
  }, [recommendation]);

  const totalDiscount = useMemo(() => {
    if (!recommendation?.bundle) {
      return 0;
    }

    return recommendation?.bundle.totalDiscount;
  }, [recommendation]);

  const totalOriginal = useMemo(() => {
    return parseFloat(
      cart.items
        .reduce((acc, item: any) => {
          if (item?.item?.NonDiscountAmount) {
            return acc + item.item.NonDiscountAmount * item.item.quantity;
          }
          return acc + item.item.total;
        }, 0.0)
        .toFixed(2),
    );
  }, [recommendation, cart.items]);

  return (
    <BundleRecommendationContext.Provider
      value={{
        disclaimer,
        recommendation,
        ticketMessages,
        parkingMessages,
        totalDiscount,
        totalOriginal,
      }}
    >
      {children}
    </BundleRecommendationContext.Provider>
  );
};
